/**
 * PinCodeModal - Modale de saisie du code PIN
 *
 * Affiche un clavier numérique pour la saisie du code PIN
 * utilisé pour l'authentification des vendeurs CASHIER_PLUS et MANAGER
 * afin d'accéder aux fonctionnalités étendues (Live Shopping, Préparation)
 */
import { useState, useCallback, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { api } from '../../services/api';

interface PinCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  vendorId: string;
  vendorName: string;
}

export default function PinCodeModal({
  isOpen,
  onClose,
  onSuccess,
  vendorId,
  vendorName,
}: PinCodeModalProps) {
  const { tenant } = useTenant();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Reset le PIN quand la modale s'ouvre
  useEffect(() => {
    if (isOpen) {
      setPin('');
      setError(null);
    }
  }, [isOpen]);

  const handleDigitPress = useCallback((digit: string) => {
    if (pin.length < 6) {
      setPin(prev => prev + digit);
      setError(null);
    }
  }, [pin.length]);

  const handleBackspace = useCallback(() => {
    setPin(prev => prev.slice(0, -1));
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (pin.length < 4) {
      setError('Le code PIN doit contenir au moins 4 chiffres');
      return;
    }

    if (!tenant?.id) {
      setError('Erreur: tenant non configuré');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await api.verifyVendorPin(vendorId, pin);

      if (result.valid) {
        onSuccess();
      } else {
        setError('Code PIN incorrect');
        setPin('');
      }
    } catch (err: any) {
      console.error('[PinCodeModal] Erreur vérification PIN:', err);
      setError(err.message || 'Erreur de vérification');
      setPin('');
    } finally {
      setLoading(false);
    }
  }, [pin, tenant?.id, vendorId, onSuccess]);

  // Auto-submit quand 4 chiffres sont saisis
  useEffect(() => {
    if (pin.length === 4) {
      handleSubmit();
    }
  }, [pin.length, handleSubmit]);

  // Gestion du clavier physique
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        handleDigitPress(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Enter' && pin.length >= 4) {
        handleSubmit();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleDigitPress, handleBackspace, handleSubmit, pin.length, onClose]);

  if (!isOpen) return null;

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', ''];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-80 max-w-[90vw]">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">Code d'accès</h2>
          <p className="text-sm text-gray-500 mt-1">
            Vendeur: <span className="font-medium text-gray-700">{vendorName}</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Accès POS + Live + Préparation
          </p>
        </div>

        {/* Affichage du PIN (masqué) */}
        <div className="flex justify-center gap-2 mb-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center text-2xl font-bold transition-all ${
                i < pin.length
                  ? 'border-blue-500 bg-blue-50 text-blue-600'
                  : 'border-gray-200 bg-gray-50 text-gray-300'
              }`}
            >
              {i < pin.length ? '•' : ''}
            </div>
          ))}
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="text-center text-red-500 text-sm mb-4 animate-shake">
            {error}
          </div>
        )}

        {/* Clavier numérique */}
        <div className="grid grid-cols-3 gap-2">
          {digits.map((digit, index) => (
            <button
              key={index}
              type="button"
              disabled={loading || digit === ''}
              onClick={() => digit && handleDigitPress(digit)}
              className={`h-14 rounded-xl text-2xl font-semibold transition-all ${
                digit === ''
                  ? 'invisible'
                  : 'bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-800'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {digit}
            </button>
          ))}
        </div>

        {/* Boutons d'action */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button
            type="button"
            onClick={handleBackspace}
            disabled={loading || pin.length === 0}
            className="h-12 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium transition-all disabled:opacity-50"
          >
            ← Effacer
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="h-12 rounded-xl bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 font-medium transition-all"
          >
            Annuler
          </button>
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="mt-4 text-center text-sm text-gray-500">
            Vérification...
          </div>
        )}
      </div>

      {/* Style pour l'animation shake */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}
